import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { AdminRole } from '../../lib/types.js';

/**
 * A user's admin roles — the whole catalog, each flagged assigned or not, so
 * the UI can render a role the tenant can't assign and explain why rather than
 * hiding it and leaving the operator wondering where it went.
 */
export const useUserAdminRolesLogic = (userId: string) => {
  const queryClient = useQueryClient();

  const query = useQuery<AdminRole[]>({
    queryKey: queryKeys.userAdminRoles(userId),
    queryFn: () => apiRequest<AdminRole[]>(`/api/users/${userId}/admin-roles`),
  });

  // Replace-all, like the scope picker's save: the checkbox list is a
  // statement of the final set, not a stream of individual toggles.
  const mutation = useMutation<AdminRole[], Error, string[]>({
    mutationFn: (roles) =>
      apiRequest<AdminRole[]>(`/api/users/${userId}/admin-roles`, {
        method: 'PUT',
        body: { roles },
      }),
    onSuccess: async (roles) => {
      queryClient.setQueryData(queryKeys.userAdminRoles(userId), roles);
      // Assigning yourself a role changes your own nav — `me` carries the
      // permissions the AppShell filters on, so it must not stay stale.
      await queryClient.invalidateQueries({ queryKey: queryKeys.me() });
    },
  });

  return {
    roles: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error ?? mutation.error,
    saveRoles: mutation.mutateAsync,
    isSubmitting: mutation.isPending,
  };
};
